import axios from 'axios';

// ALWAYS use relative /api path. The Kubernetes/Nginx proxy routes /api → backend
// on ALL domains (preview URL, custom domain, localhost). This avoids CORS entirely.
const API_BASE = '/api';

// ─── Axios Instance with Retry ─────────────────────────────────────────────────

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const apiMultipart = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  timeout: 60000,
  headers: {
    'Content-Type': 'multipart/form-data',
  },
});

// ─── Retry Interceptor ─────────────────────────────────────────────────────────

const MAX_RETRIES = 2;
const RETRY_DELAY = 1000;

function addRetryInterceptor(instance) {
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const config = error.config;
      if (!config) return Promise.reject(error);

      config.__retryCount = config.__retryCount || 0;

      // Only retry on network errors or 502/503/504 (not on 4xx client errors)
      const isRetryable =
        !error.response ||
        [502, 503, 504].includes(error.response?.status);

      if (isRetryable && config.__retryCount < MAX_RETRIES && config.method === 'get') {
        config.__retryCount += 1;
        await new Promise((r) => setTimeout(r, RETRY_DELAY * config.__retryCount));
        return instance(config);
      }

      return Promise.reject(error);
    }
  );
}

addRetryInterceptor(api);
addRetryInterceptor(apiMultipart);

// ─── Request Deduplication for GET requests ─────────────────────────────────────

const pendingRequests = new Map();

api.interceptors.request.use((config) => {
  if (config.method === 'get') {
    const key = `${config.url}${JSON.stringify(config.params || {})}`;
    if (pendingRequests.has(key)) {
      const controller = new AbortController();
      config.signal = controller.signal;
      controller.abort('Duplicate request cancelled');
      return config;
    }
    pendingRequests.set(key, true);
    config._dedupKey = key;
  }
  return config;
});

api.interceptors.response.use(
  (response) => {
    if (response.config._dedupKey) {
      pendingRequests.delete(response.config._dedupKey);
    }
    return response;
  },
  (error) => {
    if (error.config?._dedupKey) {
      pendingRequests.delete(error.config._dedupKey);
    }
    return Promise.reject(error);
  }
);

// ─── Idempotency Key Generator ──────────────────────────────────────────────────

let idempotencyCounter = 0;
export function generateIdempotencyKey() {
  idempotencyCounter += 1;
  return `${Date.now()}-${idempotencyCounter}-${Math.random().toString(36).slice(2, 8)}`;
}

// ─── API Modules ────────────────────────────────────────────────────────────────

export const authAPI = {
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
};

export const ticketAPI = {
  createTicket: (formData) => apiMultipart.post('/tickets', formData),
  getTickets: () => api.get('/tickets'),
  getTicket: (ticketId) => api.get(`/tickets/${ticketId}`),
  updateTicket: (ticketId, data) => api.patch(`/tickets/${ticketId}`, data),
  updateTicketWIP: (ticketId, data) => api.post(`/tickets/${ticketId}/update-wip`, data),
  rateTicket: (ticketId, data) => api.post(`/tickets/${ticketId}/rate`, data),
  submitFeedback: (ticketId, data) => api.post(`/tickets/${ticketId}/feedback`, data),
  getPendingFeedback: () => api.get('/tickets/pending-feedback'),
  assignSecondLine: (ticketId, data) => api.post(`/tickets/${ticketId}/assign-second-line`, data),
  reassignTicket: (ticketId, newOwnerId) => api.patch(`/tickets/${ticketId}/reassign`, null, { params: { new_owner_id: newOwnerId } }),
  sendCompletionNotification: (ticketId) => api.post(`/tickets/${ticketId}/notify-completion`),
  submitOnLeave: (ticketId, data) => api.post(`/tickets/${ticketId}/on-leave`, data),
  submitShiftCompleted: (ticketId, data) => api.post(`/tickets/${ticketId}/shift-completed`, data),
  submitMaterialStatus: (ticketId, data) => api.post(`/tickets/${ticketId}/material-status`, data),
  setResolutionTAT: (ticketId, data) => api.post(`/tickets/${ticketId}/set-tat`, data),
  divertTicket: (ticketId, data) => api.post(`/tickets/${ticketId}/divert`, data),
  createStoreRequest: (data) => api.post('/tickets/store-request', data),
};

export const analyticsAPI = {
  getOverview: () => api.get('/analytics/overview', { timeout: 45000 }), // 45s for heavy analytics
  getDailyStats: (date) => api.get('/analytics/daily', { params: { date }, timeout: 45000 }),
  getResolverActivity: () => api.get('/analytics/resolver-activity', { timeout: 45000 }),
  // Advanced analytics (CAO-only). All three accept optional ISO date range.
  getStatusTransitionTAT: (params) => api.get('/analytics/status-transition-tat', { params, timeout: 45000 }),
  getSlaBreachTAT: (params) => api.get('/analytics/sla-breach-tat', { params, timeout: 45000 }),
  getActivityLeaderboard: (params) => api.get('/analytics/activity-leaderboard', { params, timeout: 45000 }),
  // Status drilldown — fetched when an admin clicks a Ticket Status Distribution tile
  getStatusTickets: (status) => api.get('/analytics/status-tickets', { params: { status }, timeout: 45000 }),
  // Feedback analytics — visible to every admin-panel member
  getFeedbackAnalytics: (params) => api.get('/analytics/feedback', { params, timeout: 45000 }),
  // Advanced visualisation endpoints (CAO War Room charts)
  getLocationVsDepartment: () => api.get('/analytics/location-vs-department', { timeout: 45000 }),
  getDepartmentStatusTat: () => api.get('/analytics/department-status-tat', { timeout: 45000 }),
};

export const configAPI = {
  getDepartments: () => api.get('/config/departments'),
  getDepartmentsHierarchy: () => api.get('/config/departments/hierarchy'),
  getDelayReasons: () => api.get('/config/delay-reasons'),
  getDepartmentTATA: (department) => api.get(`/config/department-tata/${encodeURIComponent(department)}`),
  getStaffAssignments: () => api.get('/config/staff-assignments'),
  getStaffDirectory: () => api.get('/config/staff-directory'),
  getStaffForDepartment: (department) => api.get(`/config/staff-assignments/${encodeURIComponent(department)}`),
  getMyDepartments: () => api.get('/staff/my-departments'),
  getUrgencySLA: () => api.get('/config/urgency-sla'),
  getDivertResolverSuggestion: (department) => api.get(`/config/divert-resolver/${encodeURIComponent(department)}`),
  getDivertDepartments: () => api.get('/config/divert-departments'),
  getTicketFormConfig: () => api.get('/config/ticket-form'),
  getProblemLocations: () => api.get('/config/problem-locations'),
};

export const userAPI = {
  getUsers: () => api.get('/users'),
  updateUserRole: (userId, role, department) => {
    const params = { role };
    if (department) {
      params.department = department;
    }
    return api.patch(`/users/${userId}`, null, { params });
  },
  // Feb 2026: dry-run authorization probe. Backend endpoint expects
  // multipart/form-data (matches the ticket form's live-email-check).
  // MUST route through `apiMultipart` because `api` hard-codes
  // Content-Type: application/json which would serialize FormData as
  // an empty object and produce 422 on the server.
  validateTicketAccess: (email) => {
    const fd = new FormData();
    fd.append('email', email);
    return apiMultipart.post('/validate-email', fd);
  },
};

export const adminMgmtAPI = {
  checkAccess: () => api.get('/admin/check-access'),
  getDepartments: () => api.get('/admin/departments'),
  addDepartment: (data) => api.post('/admin/departments', data),
  editDepartment: (name, data) => api.patch(`/admin/departments/${encodeURIComponent(name)}`, data),
  deleteDepartment: (name) => api.delete(`/admin/departments/${encodeURIComponent(name)}`),
  addUser: (data) => api.post('/admin/users', data),
  editUser: (userId, data) => api.patch(`/admin/users/${userId}`, data),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  purgeTicketPhoto: (ticketId) => api.delete(`/admin/tickets/${ticketId}/photo`),
  getAuditLogs: () => api.get('/admin/audit-logs'),
  getProblemLocations: () => api.get('/admin/problem-locations'),
  addProblemLocation: (data) => api.post('/admin/problem-locations', data),
  editProblemLocation: (locationId, data) => api.patch(`/admin/problem-locations/${locationId}`, data),
  deleteProblemLocation: (locationId) => api.delete(`/admin/problem-locations/${locationId}`),
  // Resolving department CRUD — Manage Users → Main Ticket Resolving Department card
  addResolvingSub: (main, sub) => api.post('/admin/resolving-departments/sub', { main, sub }),
  renameResolvingSub: (main, oldName, newName) => api.patch('/admin/resolving-departments/sub', { main, old_name: oldName, new_name: newName }),
  deleteResolvingSub: (main, sub) => api.delete('/admin/resolving-departments/sub', { data: { main, sub } }),
  // Main-Department Admin Assignments — Manage Users → Main Department Admin Access card
  getMainDeptCatalog: () => api.get('/admin/main-departments-catalog'),
  listMainDeptAdmins: () => api.get('/admin/main-dept-admins'),
  upsertMainDeptAdmin: (email, payload) => api.put(`/admin/main-dept-admins/${encodeURIComponent(email)}`, payload),
  deleteMainDeptAdmin: (email) => api.delete(`/admin/main-dept-admins/${encodeURIComponent(email)}`),
};

export const reportsAPI = {
  getReportTypes: () => api.get('/reports/types'),
  getAnalyticsData: (period, startDate, endDate) => {
    const params = { period };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    return api.get('/reports/analytics-data', { params, timeout: 45000 });
  },
  downloadDailyReport: () => api.get('/reports/generate/daily', { responseType: 'blob', timeout: 60000 }),
  downloadWeeklyReport: () => api.get('/reports/generate/weekly', { responseType: 'blob', timeout: 60000 }),
  downloadMonthlyReport: () => api.get('/reports/generate/monthly', { responseType: 'blob', timeout: 60000 }),
  downloadCustomReport: (startDate, endDate) => api.get('/reports/generate/custom', {
    params: { start_date: startDate, end_date: endDate },
    responseType: 'blob',
    timeout: 60000,
  }),
};
